import { Users, Crown, TrendingUp, Package, Star, ScanLine, Tag, Target, Zap, MessageSquare, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { useAnalytics } from '@/lib/data'
import { UserGrowthChart } from '../Charts'
import { OrganizationId } from '@/lib/orgs'
import { HexLoader } from '@/components/ui/HexLoader'

interface AnalyticsViewProps {
  client: SupabaseClient | null
  orgSlug?: OrganizationId  // Optional: defaults to 'buyr'
}

export function AnalyticsView({ client, orgSlug = 'buyr' }: AnalyticsViewProps) {
  const { data, isLoading, isRefreshing, error, forceRefresh } = useAnalytics(client, { orgSlug })

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <TrendingUp size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase to view analytics</p>
      </div>
    )
  }

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <HexLoader size="lg" text="Loading analytics" />
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
        {error}
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-[var(--text-main)]">Analytics</h2>
          {isRefreshing && (
            <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Refreshing...</span>
            </div>
          )}
        </div>
        <button
          onClick={() => forceRefresh()}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-[var(--bg-element)] border border-[var(--border)] rounded-lg text-[var(--text-muted)] hover:text-[var(--text-main)] hover:border-[var(--primary)] transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ============ KEY PERFORMANCE INDICATORS ============ */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Target size={20} className="text-[var(--primary)]" />
          <h3 className="text-lg font-semibold text-[var(--text-main)]">Key Performance Indicators</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {/* Premium Conversion */}
          <div className="bg-[var(--bg-card)] border-2 border-amber-500/20 p-6 rounded-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center">
                <Crown className="w-6 h-6 text-amber-400" />
              </div>
              <span className="text-[var(--text-main)] font-medium">Premium Conversion</span>
            </div>
            <div className="text-4xl font-bold text-amber-400 mb-2">{data.conversionRate}%</div>
            <p className="text-[var(--text-muted)] text-sm">
              {data.premiumUsers} of {data.totalUsers.toLocaleString()} users
            </p>
            <div className={`mt-3 inline-block px-2 py-1 rounded text-xs font-medium ${
              parseFloat(data.conversionRate) > 5 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'
            }`}>
              {parseFloat(data.conversionRate) > 5 ? 'Good' : 'Needs Improvement'}
            </div>
          </div>

          {/* Spotlight Coverage */}
          <div className="bg-[var(--bg-card)] border-2 border-blue-500/20 p-6 rounded-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Star className="w-6 h-6 text-blue-400" />
              </div>
              <span className="text-[var(--text-main)] font-medium">Brand Spotlight Coverage</span>
            </div>
            <div className="text-4xl font-bold text-blue-400 mb-2">{data.spotlightCoveragePct}%</div>
            <p className="text-[var(--text-muted)] text-sm">
              {data.brandsWithSpotlight} of {data.totalBrands} brands
            </p>
            <div className={`mt-3 inline-block px-2 py-1 rounded text-xs font-medium ${
              parseFloat(data.spotlightCoveragePct) > 50 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'
            }`}>
              {parseFloat(data.spotlightCoveragePct) > 50 ? 'Excellent' : 'Growing'}
            </div>
          </div>

          {/* Platform Health */}
          <div className="bg-[var(--bg-card)] border-2 border-emerald-500/20 p-6 rounded-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-emerald-400" />
              </div>
              <span className="text-[var(--text-main)] font-medium">Platform Health</span>
            </div>
            <div className="space-y-3 mt-2">
              <div className="flex justify-between items-center p-2 bg-[var(--bg-element)] rounded-lg">
                <span className="text-sm text-[var(--text-muted)]">Content Moderation</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  parseFloat(data.contentModerationRate) < 5 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {data.contentModerationRate}%
                </span>
              </div>
              <div className="flex justify-between items-center p-2 bg-[var(--bg-element)] rounded-lg">
                <span className="text-sm text-[var(--text-muted)]">Critical Issues</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  data.criticalFeedback === 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {data.criticalFeedback}
                </span>
              </div>
              <div className="flex justify-between items-center p-2 bg-[var(--bg-element)] rounded-lg">
                <span className="text-sm text-[var(--text-muted)]">Active Content</span>
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald-400">
                  {data.activeContentPct}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-[var(--border)]" />

      {/* ============ USER METRICS ============ */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Users size={20} className="text-[var(--primary)]" />
          <h3 className="text-lg font-semibold text-[var(--text-main)]">User Metrics</h3>
        </div>
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
          <MetricCard label="Total Users" value={data.totalUsers.toLocaleString()} subtext="Registered accounts" />
          <MetricCard label="Premium Users" value={data.premiumUsers.toString()} subtext={`${data.conversionRate}% conversion`} highlight="amber" />
          <MetricCard label="Free Users" value={data.freeUsers.toString()} subtext={`${data.totalUsers > 0 ? ((data.freeUsers / data.totalUsers) * 100).toFixed(1) : 0}% of total`} />
          <MetricCard label="Avg Scans/User" value={data.avgScansPerUser} subtext="Per user lifetime" />
        </div>
      </section>

      <div className="border-t border-[var(--border)]" />

      {/* ============ USER GROWTH CHART ============ */}
      <section>
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={20} className="text-[var(--primary)]" />
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-main)]">User Growth Over Time</h3>
              <p className="text-sm text-[var(--text-muted)]">
                Cumulative user growth from {data.userGrowthData.length > 0 ? data.userGrowthData[0].date : 'launch'} to present
              </p>
            </div>
          </div>
          {data.userGrowthData.length > 0 ? (
            <div className="h-[350px]">
              <UserGrowthChart data={data.userGrowthData} />
            </div>
          ) : (
            <div className="h-[350px] flex items-center justify-center text-[var(--text-muted)]">
              <p>No user data available yet</p>
            </div>
          )}
        </div>
      </section>

      <div className="border-t border-[var(--border)]" />

      {/* ============ USER ENGAGEMENT ============ */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Zap size={20} className="text-[var(--primary)]" />
          <h3 className="text-lg font-semibold text-[var(--text-main)]">User Engagement</h3>
        </div>
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
          <MetricCard label="Total Scans" value={data.totalScans.toLocaleString()} subtext="Product scans" />
          <MetricCard label="Total Reviews" value={data.totalReviews.toLocaleString()} subtext={`Avg rating: ${data.averageRating}`} />
          <MetricCard label="Active Reviewers" value={data.uniqueReviewers.toString()} subtext={`${data.reviewEngagementRate}% of users`} highlight="purple" />
          <MetricCard label="Reviews/Reviewer" value={data.reviewsPerReviewer} subtext="Avg per active reviewer" />
          <MetricCard label="Review Engagement" value={`${data.reviewEngagementRate}%`} subtext="Users who review" highlight="blue" />
          <MetricCard label="Flagged Content" value={data.flaggedReviews.toString()} subtext={`${data.contentModerationRate}% of reviews`} highlight="orange" />
          <MetricCard label="Scan Engagement" value={data.avgScansPerUser} subtext="Scans per user" />
          <MetricCard label="Avg Rating" value={`${data.averageRating}`} subtext="Overall average" highlight="yellow" />
        </div>
      </section>

      <div className="border-t border-[var(--border)]" />

      {/* ============ BRAND OWNERSHIP DISTRIBUTION ============ */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Tag size={20} className="text-[var(--primary)]" />
          <h3 className="text-lg font-semibold text-[var(--text-main)]">Brand & Spotlight Metrics</h3>
        </div>

        <div className="grid gap-4 grid-cols-2 md:grid-cols-4 mb-6">
          <MetricCard label="Total Brands" value={data.totalBrands.toLocaleString()} subtext="In database" />
          <MetricCard label="Brands with Spotlights" value={data.brandsWithSpotlight.toString()} subtext={`${data.spotlightCoveragePct}% coverage`} highlight="blue" />
          <MetricCard label="Brands Need Spotlights" value={(data.totalBrands - data.brandsWithSpotlight).toString()} subtext="Missing content" highlight="orange" />
          <MetricCard label="Products per Brand" value={data.totalBrands > 0 ? (data.totalProducts / data.totalBrands).toFixed(1) : '0'} subtext="Average catalog size" />
        </div>

        {/* Brand Ownership Breakdown */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <h4 className="text-base font-semibold text-[var(--text-main)] mb-4">Brand Ownership Distribution</h4>
          <p className="text-sm text-[var(--text-muted)] mb-4">Breakdown by ownership type: Ethical (green), Private (orange), MegaCorp (red)</p>

          {/* Summary Cards */}
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4 mb-6">
            <OwnershipSummaryCard label="Ethical Ownership" value={data.brandOwnership.ethicalTotal} total={data.totalBrands} color="green" />
            <OwnershipSummaryCard label="Private Ownership" value={data.brandOwnership.privateTotal} total={data.totalBrands} color="orange" />
            <OwnershipSummaryCard label="MegaCorp" value={data.brandOwnership.megaCorp} total={data.totalBrands} color="red" />
            <OwnershipSummaryCard label="Unknown" value={data.brandOwnership.unknown} total={data.totalBrands} color="gray" />
          </div>

          {/* Detailed Breakdown */}
          <div className="pt-4 border-t border-[var(--border)]">
            <h5 className="text-sm font-semibold text-[var(--text-muted)] mb-3">Detailed Breakdown</h5>
            <div className="grid gap-2 grid-cols-2 md:grid-cols-4">
              <OwnershipRow label="Family Owned" value={data.brandOwnership.familyOwned} color="green" />
              <OwnershipRow label="Founder Owned" value={data.brandOwnership.founderOwned} color="green" />
              <OwnershipRow label="Employee Owned" value={data.brandOwnership.employeeOwned} color="green" />
              <OwnershipRow label="Cooperative" value={data.brandOwnership.cooperative} color="green" />
              <OwnershipRow label="Private Equity" value={data.brandOwnership.privateEquity} color="orange" />
              <OwnershipRow label="Private Owned" value={data.brandOwnership.privateOwned} color="orange" />
              <OwnershipRow label="Megacorp" value={data.brandOwnership.megaCorp} color="red" />
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-[var(--border)]" />

      {/* ============ PRODUCT OWNERSHIP DISTRIBUTION ============ */}
      <section>
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <h4 className="text-base font-semibold text-[var(--text-main)] mb-4">Product Ownership Distribution</h4>
          <p className="text-sm text-[var(--text-muted)] mb-4">Products aggregated by brand ownership type: Ethical (green), Private (orange), MegaCorp (red)</p>

          {/* Summary Cards */}
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4 mb-6">
            <OwnershipSummaryCard label="Ethical Ownership" value={data.productOwnership.ethicalTotal} total={data.totalProducts} color="green" />
            <OwnershipSummaryCard label="Private Ownership" value={data.productOwnership.privateTotal} total={data.totalProducts} color="orange" />
            <OwnershipSummaryCard label="MegaCorp" value={data.productOwnership.megaCorp} total={data.totalProducts} color="red" />
            <OwnershipSummaryCard label="Unknown" value={data.productOwnership.unknown} total={data.totalProducts} color="gray" />
          </div>

          {/* Detailed Breakdown */}
          <div className="pt-4 border-t border-[var(--border)]">
            <h5 className="text-sm font-semibold text-[var(--text-muted)] mb-3">Detailed Breakdown</h5>
            <div className="grid gap-2 grid-cols-2 md:grid-cols-4">
              <OwnershipRow label="Family Owned" value={data.productOwnership.familyOwned} color="green" />
              <OwnershipRow label="Founder Owned" value={data.productOwnership.founderOwned} color="green" />
              <OwnershipRow label="Employee Owned" value={data.productOwnership.employeeOwned} color="green" />
              <OwnershipRow label="Cooperative" value={data.productOwnership.cooperative} color="green" />
              <OwnershipRow label="Private Equity" value={data.productOwnership.privateEquity} color="orange" />
              <OwnershipRow label="Private Owned" value={data.productOwnership.privateOwned} color="orange" />
              <OwnershipRow label="Megacorp" value={data.productOwnership.megaCorp} color="red" />
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-[var(--border)]" />

      {/* ============ CONTENT & FEEDBACK ============ */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare size={20} className="text-[var(--primary)]" />
          <h3 className="text-lg font-semibold text-[var(--text-main)]">Content & Feedback</h3>
        </div>
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
          <MetricCard label="Total Products" value={data.totalProducts.toLocaleString()} subtext="In database" />
          <MetricCard label="Quizzes" value={data.totalQuizzes.toString()} subtext={`${data.activeQuizzes} active`} />
          <MetricCard label="Greetings" value={data.totalGreetings.toString()} subtext={`${data.activeGreetings} active`} />
          <MetricCard label="Beta Feedback" value={data.totalFeedback.toString()} subtext={`${data.openFeedback} open, ${data.criticalFeedback} critical`} />
        </div>
      </section>
    </div>
  )
}

interface MetricCardProps {
  label: string
  value: string
  subtext: string
  highlight?: 'amber' | 'blue' | 'purple' | 'orange' | 'yellow' | 'green'
}

function MetricCard({ label, value, subtext, highlight }: MetricCardProps) {
  const highlightColors: Record<string, string> = {
    amber: 'text-amber-400',
    blue: 'text-blue-400',
    purple: 'text-purple-400',
    orange: 'text-orange-400',
    yellow: 'text-yellow-400',
    green: 'text-emerald-400',
  }

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] p-4 rounded-xl">
      <p className="text-[var(--text-muted)] text-sm mb-1">{label}</p>
      <p className={`text-2xl font-bold ${highlight ? highlightColors[highlight] : 'text-[var(--text-main)]'}`}>
        {value}
      </p>
      <p className="text-[var(--text-muted)] text-xs mt-1">{subtext}</p>
    </div>
  )
}

interface OwnershipSummaryCardProps {
  label: string
  value: number
  total: number
  color: 'green' | 'orange' | 'red' | 'gray'
}

function OwnershipSummaryCard({ label, value, total, color }: OwnershipSummaryCardProps) {
  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0'

  const borderColors: Record<string, string> = {
    green: 'border-emerald-500/30',
    orange: 'border-orange-500/30',
    red: 'border-red-500/30',
    gray: 'border-gray-500/30',
  }

  const bgColors: Record<string, string> = {
    green: 'bg-emerald-500/10',
    orange: 'bg-orange-500/10',
    red: 'bg-red-500/10',
    gray: 'bg-gray-500/10',
  }

  const textColors: Record<string, string> = {
    green: 'text-emerald-400',
    orange: 'text-orange-400',
    red: 'text-red-400',
    gray: 'text-gray-400',
  }

  return (
    <div className={`flex items-center justify-between p-4 border-2 rounded-lg ${borderColors[color]} ${bgColors[color]}`}>
      <div>
        <div className="text-sm text-[var(--text-muted)]">{label}</div>
        <div className={`text-2xl font-bold ${textColors[color]}`}>{value}</div>
      </div>
      <span className={`px-2 py-1 rounded text-xs font-medium ${bgColors[color]} ${textColors[color]}`}>
        {percentage}%
      </span>
    </div>
  )
}

interface OwnershipRowProps {
  label: string
  value: number
  color: 'green' | 'orange' | 'red'
}

function OwnershipRow({ label, value, color }: OwnershipRowProps) {
  const bgColors: Record<string, string> = {
    green: 'bg-emerald-500/10',
    orange: 'bg-orange-500/10',
    red: 'bg-red-500/10',
  }

  const textColors: Record<string, string> = {
    green: 'text-emerald-400',
    orange: 'text-orange-400',
    red: 'text-red-400',
  }

  return (
    <div className={`flex items-center justify-between p-2 rounded ${bgColors[color]}`}>
      <span className="text-sm text-[var(--text-main)]">{label}</span>
      <span className={`font-semibold ${textColors[color]}`}>{value}</span>
    </div>
  )
}
